import { memo, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import { clearThumbnailCache } from '@air/utils-clips';

import { RiveThumbnail } from '~/components/RiveThumbnail';
import { GalleryAssetThumbnailImage } from '~/components/v2/components/shared/gallery/GalleryAssetThumbnailImage';
import { DRAG_PREVIEW_HEIGHT, DRAG_PREVIEW_WIDTH } from '~/constants/dnd';
import { getDragPreviewSrc } from '~/utils/dragPreviewImageCache';
import { getRoundedImgixDimensions } from '~/utils/getRoundedImgixDimensions';
import { isRiveFile } from '~/utils/RiveUtils';

export type DndAssetDragPreviewProps = {
  asset: Clip;
  width?: number;
  height?: number;
};

export const DNDAssetGalleryCardDragPreview = memo(
  ({ asset, width = DRAG_PREVIEW_WIDTH, height = DRAG_PREVIEW_HEIGHT }: DndAssetDragPreviewProps) => {
    const isRive = isRiveFile(asset);
    const cachedSrc = getDragPreviewSrc(asset.id);
    const imgixDimensions = useMemo(() => getRoundedImgixDimensions({ height, width }), [width, height]);

    return (
      <div className="absolute inset-1 overflow-hidden rounded-12">
        {isRive && asset.assets?.original ? (
          <RiveThumbnail borderRadius={0} height={height} src={asset.assets.original} width={width} />
        ) : cachedSrc ? (
          // biome-ignore lint/performance/noImgElement: cachedSrc is a fully-resolved Imgix URL already in browser memory — must use exact URL to avoid re-fetch
          <img alt={asset.description || ''} className="h-full w-full object-cover" src={cachedSrc} />
        ) : (
          // Fallback for assets not yet viewed (no cached src yet)
          <GalleryAssetThumbnailImage
            className="opacity-100"
            description={asset.description}
            ext={asset.ext}
            height={imgixDimensions.height}
            imgixParams={{ fit: 'crop' }}
            src={clearThumbnailCache({
              clipExt: asset.ext,
              clipStatus: asset.status,
              clipUpdatedAt: asset.updatedAt,
              imageSrc: asset.assets?.image,
            })}
            status={asset.status}
            width={imgixDimensions.width}
          />
        )}
      </div>
    );
  },
);

DNDAssetGalleryCardDragPreview.displayName = 'DNDAssetGalleryCardDragPreview';
